import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type {
  AppCatalogEntry,
  InstalledApp,
  PanelUser,
  Site
} from "../types";
import {
  AppsServiceViews,
  type AppsToolView
} from "./AppsServiceViews";

type SysApp = {
  id: string;
  name: string;
  version: string;
  category: string;
  description: string;
  homepage?: string;
  spaceMb: number;
  packages: string[];
  notes: string[];
  features: string[];
  kind: string;
};

type SysCategory = { id: string; label: string; count: number };

type InstalledSys = {
  id: string;
  appId: string;
  appName: string;
  version: string;
  category: string;
  status: string;
  packages?: string[];
  live?: { installed: boolean; detail: string };
};

/**
 * Webuzo-style Apps: system software sidebar + website CMS installer.
 */
function mapToolToView(toolId?: string | null, hint?: string | null): AppsToolView {
  const key = (hint || toolId || "install").toLowerCase();
  const map: Record<string, AppsToolView> = {
    "install-apps": "install",
    install: "install",
    "install-distro": "install",
    "installed-apps": "installed",
    installed: "installed",
    "update-apps": "update",
    update: "update",
    "apps-services": "services",
    services: "services",
    "enduser-apps": "website",
    "website-apps": "website",
    website: "website",
    "directory-index": "directory-index",
    "default-apps": "default-apps",
    "apache-status": "apache-status",
    "apache-info": "apache-info",
    "apache-settings": "apache-settings",
    "apache-handlers": "apache-handlers",
    "apache-modules": "apache-modules",
    "php-extensions": "php-extensions",
    "php-config": "php-extensions",
    multiphp: "multiphp",
    "multiphp-manager": "multiphp",
    "multiphp-domains": "multiphp",
    "php-fpm": "php-fpm",
    "php-fpm-settings": "php-fpm",
    "php-pear": "php-pear",
    "php-ini": "php-ini",
    "edit-php-ini": "php-ini",
    "mysql-config": "mysql-config",
    "perl-module": "perl-module",
    "ftp-config": "ftp-config"
  };
  return map[key] || "install";
}

export function AppsPage({
  accounts,
  sites,
  currentUser,
  onRefreshAccounts,
  initialMode,
  toolId
}: {
  accounts: PanelUser[];
  sites: Site[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
  /** From WHM sidebar tool: install | installed | website */
  initialMode?: "install" | "installed" | "website" | string;
  toolId?: string | null;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";

  const [view, setView] = useState<AppsToolView>(() =>
    staff
      ? mapToolToView(toolId, initialMode)
      : "website"
  );

  useEffect(() => {
    if (!staff) {
      setView("website");
      return;
    }
    setView(mapToolToView(toolId, initialMode));
  }, [toolId, initialMode, staff]);

  // legacy mode aliases for chips
  const mode: "install" | "installed" | "website" =
    view === "website"
      ? "website"
      : view === "installed" || view === "update"
        ? "installed"
        : view === "install"
          ? "install"
          : "install";
  const setMode = (m: "install" | "installed" | "website") => {
    setView(m === "website" ? "website" : m === "installed" ? "installed" : "install");
  };
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [categories, setCategories] = useState<SysCategory[]>([]);
  const [sysApps, setSysApps] = useState<SysApp[]>([]);
  const [sysInstalled, setSysInstalled] = useState<InstalledSys[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<
    "overview" | "notes" | "features"
  >("overview");
  const [sysStatus, setSysStatus] = useState<Record<string, unknown> | null>(
    null
  );
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  // website apps (existing)
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? provisioned[0]?.id || ""
      : currentUser.provisionStatus === "ready"
        ? currentUser.id
        : ""
  );
  const [webCatalog, setWebCatalog] = useState<AppCatalogEntry[]>([]);
  const [webInstalled, setWebInstalled] = useState<InstalledApp[]>([]);
  const [appId, setAppId] = useState("wordpress");
  const [domain, setDomain] = useState("");
  const [path, setPath] = useState("");
  const [siteTitle, setSiteTitle] = useState("");
  const [adminUser, setAdminUser] = useState("admin");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [shownPass, setShownPass] = useState("");
  const [installResult, setInstallResult] = useState<{
    url?: string;
    password?: string;
    appId?: string;
    domain?: string;
    path?: string;
  } | null>(null);

  const userSites = sites.filter((s) => s.userId === userId);

  const loadSystem = useCallback(async () => {
    if (!staff) return;
    setError("");
    try {
      const cat = await api<{
        categories: SysCategory[];
        items: SysApp[];
      }>("/api/v1/system-apps/catalog");
      setCategories(cat.categories || []);
      setSysApps(cat.items || []);
      // Keep list view by default — user picks an app from the grid
      const inst = await api<{ items: InstalledSys[] }>(
        "/api/v1/system-apps/installed"
      );
      setSysInstalled(inst.items || []);
      const st = await api<{ status: Record<string, unknown> }>(
        "/api/v1/system-apps/status"
      );
      setSysStatus(st.status || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [staff]);

  const loadWebsite = useCallback(async () => {
    if (!userId && !staff) return;
    try {
      const c = await api<{ items: AppCatalogEntry[] }>(
        "/api/v1/apps/catalog"
      );
      setWebCatalog(c.items || []);
      if (userId) {
        const i = await api<{ items: InstalledApp[] }>(
          `/api/v1/apps?userId=${encodeURIComponent(userId)}`
        );
        setWebInstalled(i.items || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, staff]);

  useEffect(() => {
    if (staff && (mode === "install" || mode === "installed")) {
      void loadSystem();
    }
    if (mode === "website") void loadWebsite();
  }, [mode, loadSystem, loadWebsite, staff]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  useEffect(() => {
    if (!userSites.length) {
      if (domain) setDomain("");
      return;
    }
    // Keep domain bound to this account's sites; auto-pick primary/first.
    const stillValid = userSites.some((s) => s.domain === domain);
    if (!stillValid) {
      const primary =
        userSites.find((s) => s.type === "primary") || userSites[0];
      setDomain(primary.domain);
      setPath(""); // domain root — never re-ask for docroot
    }
  }, [userSites, domain]);

  const filtered = useMemo(() => {
    let list = sysApps;
    if (category !== "all") {
      const label =
        categories.find((c) => c.id === category)?.label || category;
      list = list.filter(
        (a) =>
          a.category === label ||
          a.category.toLowerCase().replace(/\s+/g, "-") === category
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.id.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          a.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [sysApps, category, search, categories]);

  const selected = useMemo(
    () => sysApps.find((a) => a.id === selectedId) || null,
    [sysApps, selectedId]
  );

  const installedSet = useMemo(() => {
    const s = new Set<string>();
    for (const i of sysInstalled) {
      if (i.status === "installed") s.add(i.appId);
    }
    return s;
  }, [sysInstalled]);

  const installSys = async (id: string) => {
    setBusy(id);
    setError("");
    setMsg("");
    try {
      await api(`/api/v1/system-apps/${encodeURIComponent(id)}/install`, {
        method: "POST",
        body: JSON.stringify({ appId: id })
      });
      setMsg(`Installed ${id}`);
      await loadSystem();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const uninstallSys = async (appId: string) => {
    if (!confirm(`Remove ${appId} packages from this server?`)) return;
    setBusy(appId);
    try {
      await api(`/api/v1/system-apps/${encodeURIComponent(appId)}`, {
        method: "DELETE"
      });
      setMsg(`Removed ${appId}`);
      await loadSystem();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const installWebsite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy("web");
    setError("");
    setMsg("");
    setShownPass("");
    setInstallResult(null);
    // Path is always relative to the selected domain document root.
    // Empty = domain root (auto). Never send the docroot path itself.
    const relPath = (path || "").trim().replace(/^\/+|\/+$/g, "");
    try {
      const res = await api<{
        password?: string;
        adminPasswordOnce?: string;
        result?: { note?: string; url?: string; path?: string };
        url?: string;
      }>("/api/v1/apps/install", {
        method: "POST",
        body: JSON.stringify({
          userId,
          appId,
          domain,
          path: relPath || undefined,
          siteTitle: siteTitle || undefined,
          adminUser,
          adminPassword: adminPassword || undefined,
          adminEmail: adminEmail || undefined,
          createSite: true
        })
      });
      const pass = res.password || res.adminPasswordOnce;
      if (pass) setShownPass(pass);
      const url = res.result?.url || res.url;
      setInstallResult({
        url,
        password: pass,
        appId,
        domain,
        path: relPath || "/"
      });
      setMsg(
        `Installed ${appId}` + (url ? ` · ${url}` : "")
      );
      setAdminPassword("");
      await loadWebsite();
      await onRefreshAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  // End-user: only website apps
  if (!staff) {
    return (
      <WebsiteAppsSection
        catalog={webCatalog}
        installed={webInstalled}
        userId={userId}
        userSites={userSites}
        entryToolId={toolId || null}
        appId={appId}
        setAppId={setAppId}
        domain={domain}
        setDomain={(d) => {
          setDomain(d);
          setPath("");
          setError("");
          setInstallResult(null);
        }}
        path={path}
        setPath={setPath}
        siteTitle={siteTitle}
        setSiteTitle={setSiteTitle}
        adminUser={adminUser}
        setAdminUser={setAdminUser}
        adminPassword={adminPassword}
        setAdminPassword={setAdminPassword}
        adminEmail={adminEmail}
        setAdminEmail={setAdminEmail}
        busy={busy === "web"}
        error={error}
        msg={msg}
        shownPass={shownPass}
        installResult={installResult}
        onClearResult={() => setInstallResult(null)}
        onInstall={installWebsite}
        onRefresh={() => void loadWebsite()}
        onUninstall={async (id, name) => {
          if (!confirm(`Uninstall ${name}?`)) return;
          await api(`/api/v1/apps/${id}`, { method: "DELETE" });
          await loadWebsite();
        }}
      />
    );
  }

  // Service / config tools (Apache, PHP, MySQL, FTP…) — not the installer grid
  const isInstallerView =
    view === "install" || view === "installed" || view === "update" || view === "website";

  if (!isInstallerView) {
    return <AppsServiceViews view={view} />;
  }

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Apps</h1>
          <p>
            Install system software · stacks · PHP · databases · web servers
            (Webuzo-style)
          </p>
        </div>
        <div className="topbar-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              if (mode === "website") void loadWebsite();
              else void loadSystem();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="chip-row" style={{ marginBottom: 14 }}>
        {(
          [
            ["install", "Install an App"],
            ["installed", "Installed Apps"],
            ["website", "Enduser Apps"]
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`chip ${mode === k ? "chip-active" : ""}`}
            onClick={() => setMode(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {sysStatus && mode !== "website" ? (
        <div className="card full" style={{ marginBottom: 12 }}>
          <p className="card-meta">
            Catalog {String(sysStatus.catalogCount)} apps ·{" "}
            {String(sysStatus.categoryCount)} categories · installed{" "}
            {String(sysStatus.installCount)} · package manager{" "}
            <span className="mono">{String(sysStatus.packageManager)}</span>
          </p>
          <p className="card-meta faint">{String(sysStatus.note || "")}</p>
        </div>
      ) : null}

      {mode === "installed" || view === "update" ? (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>App</th>
                <th>Category</th>
                <th>Version</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sysInstalled.length === 0 ? (
                <tr>
                  <td colSpan={5} className="faint">
                    No system apps installed or detected
                  </td>
                </tr>
              ) : (
                sysInstalled.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <strong>{i.appName}</strong>
                      <div className="faint mono" style={{ fontSize: 11 }}>
                        {i.appId}
                      </div>
                    </td>
                    <td className="faint">{i.category}</td>
                    <td className="mono faint">{i.version}</td>
                    <td>
                      <span className="badge status-active">{i.status}</span>
                      {i.live?.detail ? (
                        <div className="faint" style={{ fontSize: 11 }}>
                          {i.live.detail}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setSelectedId(i.appId);
                          setMode("install");
                        }}
                      >
                        Open
                      </button>
                      {!String(i.id).startsWith("detected-") ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => void uninstallSys(i.appId)}
                        >
                          Uninstall
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {mode === "install" ? (
        <div className="apps-layout">
          {/* Left: categories only */}
          <aside className="apps-side">
            <div className="apps-side-head">Categories</div>
            <button
              type="button"
              className={`apps-cat ${category === "all" ? "is-active" : ""}`}
              onClick={() => {
                setCategory("all");
                setSelectedId(null);
              }}
            >
              <span className="apps-cat-label">All apps</span>
              <span className="apps-cat-count">{sysApps.length}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`apps-cat ${category === c.id ? "is-active" : ""}`}
                onClick={() => {
                  setCategory(c.id);
                  setSelectedId(null);
                }}
              >
                <span className="apps-cat-label">{c.label}</span>
                <span className="apps-cat-count">{c.count}</span>
              </button>
            ))}
          </aside>

          {/* Center: list or detail */}
          <section className="apps-main">
            <div className="apps-toolbar">
              <input
                type="search"
                className="apps-search"
                placeholder="Search Apps…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (e.target.value) setSelectedId(null);
                }}
              />
              <span className="apps-toolbar-meta">
                {filtered.length} app{filtered.length === 1 ? "" : "s"}
                {category !== "all"
                  ? ` · ${categories.find((c) => c.id === category)?.label || category}`
                  : ""}
              </span>
            </div>

            {selected ? (
              <article className="apps-detail">
                <header className="apps-detail-head">
                  <div className="apps-detail-title-row">
                    <div className="apps-detail-icon" aria-hidden>
                      {selected.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="apps-detail-name">
                        {selected.name}
                        <span className="apps-stars" aria-hidden>
                          ★★★★☆
                        </span>
                      </h2>
                      <p className="apps-detail-meta">
                        Version {selected.version}
                        <span className="apps-dot">·</span>
                        {selected.category}
                        {selected.homepage ? (
                          <>
                            <span className="apps-dot">·</span>
                            <a
                              href={selected.homepage}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Homepage
                            </a>
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>
                  <div className="apps-detail-actions">
                    {installedSet.has(selected.id) ? (
                      <span className="badge status-active">Installed</span>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy === selected.id}
                      onClick={() => void installSys(selected.id)}
                    >
                      {busy === selected.id
                        ? "Installing…"
                        : installedSet.has(selected.id)
                          ? "Reinstall"
                          : "Install"}
                    </button>
                    {installedSet.has(selected.id) ? (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void uninstallSys(selected.id)}
                      >
                        Uninstall
                      </button>
                    ) : null}
                  </div>
                </header>

                <div className="apps-tabs">
                  {(
                    [
                      ["overview", "Overview"],
                      ["notes", "Notes"],
                      ["features", "Features"]
                    ] as const
                  ).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className={`apps-tab ${detailTab === k ? "is-active" : ""}`}
                      onClick={() => setDetailTab(k)}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="apps-detail-body">
                  {detailTab === "overview" ? (
                    <>
                      <p className="apps-desc">{selected.description}</p>
                      <div className="apps-info-grid">
                        <div className="apps-info-card">
                          <div className="apps-info-title">Space Required</div>
                          <p>
                            Required space: <strong>{selected.spaceMb} MB</strong>
                          </p>
                          <p className="apps-packages">
                            Packages:{" "}
                            <code>
                              {selected.packages?.length
                                ? selected.packages.join(", ")
                                : "none mapped (third-party repo may be needed)"}
                            </code>
                          </p>
                        </div>
                        <div className="apps-info-card">
                          <div className="apps-info-title">Software Support</div>
                          <p>
                            Installed with the server package manager (apt/dnf).
                            Not every version is in the default Ubuntu/RHEL repos.
                          </p>
                        </div>
                      </div>
                    </>
                  ) : null}
                  {detailTab === "notes" ? (
                    <ul className="apps-list-bullets">
                      {(selected.notes || []).map((n, i) => (
                        <li key={i}>{n}</li>
                      ))}
                    </ul>
                  ) : null}
                  {detailTab === "features" ? (
                    <ul className="apps-list-bullets">
                      {(selected.features || []).map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <footer className="apps-detail-foot">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelectedId(null)}
                  >
                    ← Back to list
                  </button>
                </footer>
              </article>
            ) : (
              <div className="apps-grid">
                {filtered.length === 0 ? (
                  <div className="apps-empty">No apps match your search.</div>
                ) : (
                  filtered.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="apps-card"
                      onClick={() => {
                        setSelectedId(a.id);
                        setDetailTab("overview");
                      }}
                    >
                      <div className="apps-card-icon" aria-hidden>
                        {a.name.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="apps-card-body">
                        <div className="apps-card-title">
                          {a.name}
                          {installedSet.has(a.id) ? (
                            <span className="apps-card-badge">installed</span>
                          ) : null}
                        </div>
                        <div className="apps-card-sub">
                          {a.category} · v{a.version} · {a.spaceMb} MB
                        </div>
                      </div>
                      <span
                        className="apps-card-install"
                        onClick={(e) => {
                          e.stopPropagation();
                          void installSys(a.id);
                        }}
                        role="presentation"
                      >
                        {busy === a.id ? "…" : "Install"}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </section>

          {/* Right: apps in active category */}
          <aside className="apps-browse">
            <div className="apps-side-head">Browse</div>
            <input
              type="search"
              className="apps-search apps-search-sm"
              placeholder="Search Apps…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (e.target.value) setSelectedId(null);
              }}
            />
            <div className="apps-browse-list">
              {(category === "all"
                ? categories
                : categories.filter((c) => c.id === category)
              ).map((c) => {
                const appsInCat = sysApps.filter((a) => a.category === c.label);
                const q = search.trim().toLowerCase();
                const shown = (q
                  ? appsInCat.filter(
                      (a) =>
                        a.name.toLowerCase().includes(q) ||
                        a.id.toLowerCase().includes(q)
                    )
                  : appsInCat
                ).slice(0, category === "all" ? 8 : 80);
                return (
                  <div key={c.id} className="apps-browse-group">
                    <button
                      type="button"
                      className={`apps-browse-group-title ${
                        category === c.id ? "is-active" : ""
                      }`}
                      onClick={() => {
                        setCategory(c.id);
                        setSelectedId(null);
                      }}
                    >
                      {c.label}
                      <span>{c.count}</span>
                    </button>
                    <ul className="apps-browse-items">
                      {shown.map((a) => (
                        <li key={a.id}>
                          <button
                            type="button"
                            className={`apps-browse-item ${
                              selectedId === a.id ? "is-active" : ""
                            }`}
                            onClick={() => {
                              setCategory(c.id);
                              setSelectedId(a.id);
                              setDetailTab("overview");
                            }}
                          >
                            <span className="apps-browse-dash">–</span>
                            <span className="apps-browse-name">{a.name}</span>
                            {installedSet.has(a.id) ? (
                              <span className="apps-browse-on">✓</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                      {appsInCat.length > shown.length ? (
                        <li className="apps-browse-more">
                          +{appsInCat.length - shown.length} more — select
                          category
                        </li>
                      ) : null}
                    </ul>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      ) : null}

      {mode === "website" ? (
        <WebsiteAppsSection
          catalog={webCatalog}
          installed={webInstalled}
          userId={userId}
          staff
          provisioned={provisioned}
          setUserId={(id) => {
            setUserId(id);
            setDomain("");
            setPath("");
            setInstallResult(null);
          }}
          userSites={userSites}
          appId={appId}
          setAppId={setAppId}
          domain={domain}
          setDomain={(d) => {
            setDomain(d);
            setPath("");
            setError("");
            setInstallResult(null);
          }}
          path={path}
          setPath={setPath}
          siteTitle={siteTitle}
          setSiteTitle={setSiteTitle}
          adminUser={adminUser}
          setAdminUser={setAdminUser}
          adminPassword={adminPassword}
          setAdminPassword={setAdminPassword}
          adminEmail={adminEmail}
          setAdminEmail={setAdminEmail}
          busy={busy === "web"}
          error={error}
          msg={msg}
          shownPass={shownPass}
          installResult={installResult}
          onClearResult={() => setInstallResult(null)}
          onInstall={installWebsite}
          onRefresh={() => void loadWebsite()}
          onUninstall={async (id, name) => {
            if (!confirm(`Uninstall ${name}?`)) return;
            await api(`/api/v1/apps/${id}`, { method: "DELETE" });
            await loadWebsite();
          }}
        />
      ) : null}

    </>
  );
}

const WEB_APP_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "cms", label: "CMS" },
  { id: "frameworks", label: "Frameworks" },
  { id: "database tools", label: "Database Tools" },
  { id: "e-commerce", label: "E-commerce" },
  { id: "developer tools", label: "Developer Tools" },
  { id: "tools", label: "Tools" },
  { id: "other", label: "Other" }
] as const;

const INSTALL_STEPS = [
  "Checking domain",
  "Validating directory",
  "Creating database",
  "Uploading files",
  "Configuring application",
  "Finalizing"
] as const;

function normalizeRelPath(raw: string): string {
  return String(raw || "")
    .trim()
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .replace(/\\/g, "/");
}

function locationKey(domain: string, path: string): string {
  const p = normalizeRelPath(path);
  return `${domain.toLowerCase()}|${p || "/"}`;
}

function passwordStrength(pw: string): { label: string; level: number } {
  if (!pw) return { label: "Auto-generate", level: 0 };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: "Weak", level: 1 };
  if (score <= 3) return { label: "Medium", level: 2 };
  return { label: "Strong", level: 3 };
}

function generatePassword(len = 16): string {
  const chars =
    "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join("");
}

function WebsiteAppsSection(props: {
  catalog: AppCatalogEntry[];
  installed: InstalledApp[];
  userId: string;
  staff?: boolean;
  provisioned?: PanelUser[];
  setUserId?: (id: string) => void;
  userSites: Site[];
  /** Sidebar / home tool id (apps, softaculous, …) — controls Browse vs Installed tab */
  entryToolId?: string | null;
  appId: string;
  setAppId: (id: string) => void;
  domain: string;
  setDomain: (v: string) => void;
  path: string;
  setPath: (v: string) => void;
  siteTitle: string;
  setSiteTitle: (v: string) => void;
  adminUser: string;
  setAdminUser: (v: string) => void;
  adminPassword: string;
  setAdminPassword: (v: string) => void;
  adminEmail: string;
  setAdminEmail: (v: string) => void;
  busy: boolean;
  error: string;
  msg: string;
  shownPass: string;
  installResult: {
    url?: string;
    password?: string;
    appId?: string;
    domain?: string;
    path?: string;
  } | null;
  onClearResult: () => void;
  onInstall: (e: FormEvent) => void;
  onRefresh: () => void;
  onUninstall: (id: string, name: string) => Promise<void>;
}) {
  const entry = (props.entryToolId || "").toLowerCase();
  const [tab, setTab] = useState<"browse" | "installed" | "updates">(() =>
    entry === "softaculous" || entry === "installed-apps"
      ? "installed"
      : entry === "update-apps"
        ? "updates"
        : "browse"
  );

  // Sync tab when opening from home icon (Application Manager → Installed)
  useEffect(() => {
    const e = (props.entryToolId || "").toLowerCase();
    if (e === "softaculous" || e === "installed-apps") {
      setTab("installed");
    } else if (e === "update-apps") {
      setTab("updates");
    } else if (e === "apps" || e === "enduser-apps" || e === "website-apps") {
      setTab("browse");
    }
  }, [props.entryToolId]);
  const [appSearch, setAppSearch] = useState("");
  const [appCat, setAppCat] = useState("all");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [mobileStep, setMobileStep] = useState<"list" | "install">("list");
  const [progressStep, setProgressStep] = useState(0);
  const [ssoBusyId, setSsoBusyId] = useState<string | null>(null);
  const [ssoNote, setSsoNote] = useState("");

  const openWpAdminSso = async (appId: string) => {
    setSsoBusyId(appId);
    setSsoNote("");
    try {
      const res = await api<{
        ok: boolean;
        url: string;
        expiresAt: string;
        expiresIn: number;
      }>(`/api/v1/apps/${encodeURIComponent(appId)}/wp-sso`, {
        method: "POST",
        body: "{}"
      });
      if (res.url) {
        window.open(res.url, "_blank", "noopener,noreferrer");
        const mins = Math.round((res.expiresIn || 1800) / 60);
        setSsoNote(
          `WP Admin auto-login opened. Link expires in ${mins} minutes (one-time use).`
        );
      }
    } catch (err) {
      setSsoNote(
        err instanceof Error ? err.message : "Could not create WP Admin link"
      );
    } finally {
      setSsoBusyId(null);
    }
  };

  const selected = props.catalog.find((c) => c.id === props.appId);
  const selectedSite = props.userSites.find((s) => s.domain === props.domain);
  const relPath = normalizeRelPath(props.path);
  const finalUrl = props.domain
    ? relPath
      ? `${props.domain}/${relPath}`
      : props.domain
    : "";
  const docRootHint = selectedSite
    ? `~/${selectedSite.documentRoot || selectedSite.domain}`
    : props.domain
      ? `~/${props.domain}`
      : "—";

  const installedAtLocation = useMemo(() => {
    const key = locationKey(props.domain, relPath);
    return props.installed.find(
      (a) =>
        a.status !== "failed" &&
        locationKey(a.domain, a.path === "/" ? "" : a.path) === key
    );
  }, [props.installed, props.domain, relPath]);

  const pathConflict = Boolean(installedAtLocation);
  const pathFieldError = pathConflict
    ? `Already installed: ${installedAtLocation!.appName}. Choose another path or uninstall first.`
    : props.error &&
        /path|directory|not empty|PATH_NOT_EMPTY/i.test(props.error)
      ? props.error
      : "";
  const pageError =
    props.error && !pathFieldError ? props.error : "";

  const countsByApp = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of props.installed) {
      if (a.status === "failed") continue;
      m.set(a.appId, (m.get(a.appId) || 0) + 1);
    }
    return m;
  }, [props.installed]);

  const filteredCatalog = useMemo(() => {
    let list = props.catalog;
    if (appCat !== "all") {
      list = list.filter((c) => {
        const cat = (c.category || "other").toLowerCase();
        if (appCat === "tools") return cat.includes("tool") || cat === "tools";
        if (appCat === "frameworks") return cat.includes("framework");
        if (appCat === "database tools")
          return cat.includes("database") || cat.includes("sql");
        if (appCat === "e-commerce")
          return cat.includes("commerce") || cat.includes("shop");
        if (appCat === "developer tools")
          return cat.includes("developer") || cat.includes("dev");
        return cat === appCat || cat.includes(appCat);
      });
    }
    if (appSearch.trim()) {
      const q = appSearch.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.id.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q)
      );
    }
    return list;
  }, [props.catalog, appCat, appSearch]);

  const updates = useMemo(
    () => props.installed.filter((a) => a.updateAvailable),
    [props.installed]
  );
  const activeCount = props.installed.filter(
    (a) => a.status === "installed" || a.status === "active"
  ).length;
  const drawer = drawerId
    ? props.installed.find((a) => a.id === drawerId) || null
    : null;
  const strength = passwordStrength(props.adminPassword);

  useEffect(() => {
    if (!props.busy) {
      setProgressStep(0);
      return;
    }
    setProgressStep(1);
    const timers = [1, 2, 3, 4, 5].map((n, i) =>
      window.setTimeout(() => setProgressStep(n + 1), (i + 1) * 900)
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [props.busy]);

  useEffect(() => {
    if (props.installResult) setTab("installed");
  }, [props.installResult]);

  const selectApp = (id: string) => {
    props.setAppId(id);
    props.onClearResult();
    setMobileStep("install");
  };

  const onDomainChange = (d: string) => {
    // Domain document root is automatic — only subdirectory path is user input.
    props.setDomain(d);
    props.setPath("");
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="wa-page">
      <div className="wa-header">
        <div>
          <div className="wa-crumb">Softaculous · Website Apps</div>
          <h1 className="wa-title">Website Apps</h1>
          <p className="wa-sub">
            Install and manage CMS, frameworks and server applications.
          </p>
        </div>
        <div className="wa-header-actions">
          {props.staff && props.setUserId && props.provisioned ? (
            <select
              className="wa-account-select"
              value={props.userId}
              onChange={(e) => props.setUserId!(e.target.value)}
              aria-label="Account"
            >
              {props.provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={props.onRefresh}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="wa-tabs" role="tablist">
        {(
          [
            ["browse", "Browse"],
            ["installed", "Installed"],
            ["updates", "Updates"]
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`wa-tab ${tab === k ? "is-active" : ""}`}
            onClick={() => setTab(k)}
          >
            {label}
            {k === "installed" && props.installed.length > 0 ? (
              <span className="wa-tab-count">{props.installed.length}</span>
            ) : null}
            {k === "updates" && updates.length > 0 ? (
              <span className="wa-tab-count wa-tab-count-warn">
                {updates.length}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {pageError ? (
        <div className="wa-alert wa-alert-danger" role="alert">
          <strong>Installation failed</strong>
          <span>{pageError}</span>
        </div>
      ) : null}
      {props.msg && !props.installResult ? (
        <div className="wa-alert wa-alert-ok">{props.msg}</div>
      ) : null}
      {props.shownPass && !props.installResult ? (
        <div className="wa-alert wa-alert-ok">
          Admin password: <code className="mono">{props.shownPass}</code>
        </div>
      ) : null}

      {!props.userId ? (
        <p className="card-meta">Select or provision an account first.</p>
      ) : null}

      {props.userId && tab === "browse" ? (
        <div
          className={`wa-workspace ${mobileStep === "install" ? "wa-mobile-install" : ""}`}
        >
          <aside className="wa-browser">
            <input
              type="search"
              className="wa-search"
              placeholder="Search applications…"
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
            />
            <div className="wa-cats">
              {WEB_APP_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`wa-cat ${appCat === c.id ? "is-active" : ""}`}
                  onClick={() => setAppCat(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="wa-app-list">
              {filteredCatalog.length === 0 ? (
                <div className="wa-empty-list">No applications match.</div>
              ) : (
                filteredCatalog.map((c) => {
                  const n = countsByApp.get(c.id) || 0;
                  const active = props.appId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`wa-app-row ${active ? "is-active" : ""}`}
                      onClick={() => selectApp(c.id)}
                    >
                      <span className="wa-app-icon" aria-hidden>
                        {c.name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="wa-app-meta">
                        <span className="wa-app-name">{c.name}</span>
                        <span className="wa-app-sub">
                          {c.category}
                          {c.version ? ` · v${c.version}` : ""}
                          {n > 0 ? ` · ${n} installed` : ""}
                        </span>
                      </span>
                      {active ? <span className="wa-app-check">✓</span> : null}
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="wa-install">
            <button
              type="button"
              className="wa-back-mobile"
              onClick={() => setMobileStep("list")}
            >
              ← Applications
            </button>

            {props.installResult &&
            props.installResult.appId === props.appId ? (
              <div className="wa-success-panel">
                <div className="wa-success-badge">Installed</div>
                <h2 className="wa-panel-title">
                  {selected?.name || "App"} Installed
                </h2>
                <p className="wa-panel-desc mono">
                  {props.installResult.domain}
                  {props.installResult.path &&
                  props.installResult.path !== "/"
                    ? `/${props.installResult.path}`
                    : ""}
                </p>
                {props.installResult.password ? (
                  <p className="wa-panel-desc">
                    Admin password:{" "}
                    <code className="mono">
                      {props.installResult.password}
                    </code>
                  </p>
                ) : null}
                <div className="wa-success-actions">
                  {props.installResult.domain ? (
                    <a
                      className="btn btn-primary btn-sm"
                      href={`https://${props.installResult.domain}${
                        props.installResult.path &&
                        props.installResult.path !== "/"
                          ? `/${props.installResult.path}`
                          : ""
                      }/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Website
                    </a>
                  ) : null}
                  {props.installResult.appId === "wordpress" &&
                  props.installResult.domain ? (
                    <a
                      className="btn btn-ghost btn-sm"
                      href={`https://${props.installResult.domain}${
                        props.installResult.path &&
                        props.installResult.path !== "/"
                          ? `/${props.installResult.path}`
                          : ""
                      }/wp-admin/`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open Admin
                    </a>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      props.onClearResult();
                      setTab("installed");
                    }}
                  >
                    Manage Installation
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      props.onClearResult();
                      props.setPath("");
                    }}
                  >
                    Install Another App
                  </button>
                </div>
              </div>
            ) : selected ? (
              <form className="wa-form" onSubmit={props.onInstall}>
                <header className="wa-form-head">
                  <div className="wa-form-icon" aria-hidden>
                    {selected.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <h2 className="wa-panel-title">
                      Install {selected.name}
                    </h2>
                    <p className="wa-panel-meta">
                      {selected.category}
                      {selected.version
                        ? ` · v${selected.version}`
                        : " · Latest stable"}
                    </p>
                  </div>
                </header>
                <p className="wa-panel-desc">{selected.description}</p>

                <div className="wa-req-grid">
                  <div className="wa-req">
                    <span className="wa-req-label">PHP</span>
                    <span className="wa-req-val">
                      {selected.requiresPhp
                        ? selectedSite?.phpVersion || "8.x"
                        : "N/A"}
                    </span>
                    <span className="wa-req-ok">✓ Ready</span>
                  </div>
                  <div className="wa-req">
                    <span className="wa-req-label">Database</span>
                    <span className="wa-req-val">
                      {selected.requiresDb ? "MySQL" : "None"}
                    </span>
                    <span className="wa-req-ok">✓ Ready</span>
                  </div>
                  <div className="wa-req">
                    <span className="wa-req-label">Storage</span>
                    <span className="wa-req-val">500 MB+</span>
                    <span className="wa-req-ok">✓ Ready</span>
                  </div>
                  <div className="wa-req">
                    <span className="wa-req-label">SSL</span>
                    <span className="wa-req-val">
                      {selectedSite?.sslEnabled
                        ? "Active"
                        : "Recommended"}
                    </span>
                    <span
                      className={
                        selectedSite?.sslEnabled ? "wa-req-ok" : "wa-req-warn"
                      }
                    >
                      {selectedSite?.sslEnabled ? "✓ Ready" : "○ Optional"}
                    </span>
                  </div>
                </div>

                <div className="wa-section">
                  <h3 className="wa-section-title">Location</h3>
                  <div className="wa-fields">
                    <div className="field wa-field">
                      <label>Domain</label>
                      {props.userSites.length ? (
                        <select
                          value={props.domain}
                          onChange={(e) => onDomainChange(e.target.value)}
                          disabled={props.busy}
                          required
                        >
                          {props.userSites.map((s) => (
                            <option key={s.id} value={s.domain}>
                              {s.domain}
                              {s.type === "primary" ? " (Primary)" : ""}
                              {s.sslEnabled ? " · SSL" : ""}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={props.domain}
                          onChange={(e) => onDomainChange(e.target.value)}
                          required
                          disabled={props.busy}
                          placeholder="example.com"
                        />
                      )}
                      {selectedSite ? (
                        <span className="wa-field-hint">
                          {selectedSite.type === "primary"
                            ? "Primary"
                            : selectedSite.type}{" "}
                          · Document root{" "}
                          <span className="mono">{docRootHint}</span>
                          {selectedSite.sslEnabled ? " · SSL Active" : ""}
                        </span>
                      ) : (
                        <span className="wa-field-hint">
                          Document root is selected automatically for the
                          domain.
                        </span>
                      )}
                    </div>

                    <div
                      className={`field wa-field ${pathFieldError ? "wa-field-error" : ""}`}
                    >
                      <label>Installation path</label>
                      <div className="wa-path-row">
                        <span className="wa-path-prefix mono">
                          {props.domain || "domain"}/
                        </span>
                        <input
                          className="mono"
                          value={props.path}
                          onChange={(e) =>
                            props.setPath(
                              e.target.value.replace(/^\/+/, "")
                            )
                          }
                          placeholder="leave empty for domain root"
                          disabled={props.busy}
                          aria-invalid={Boolean(pathFieldError)}
                        />
                      </div>
                      <span className="wa-field-hint">
                        Installs into the domain document root
                        {relPath ? (
                          <>
                            {" "}
                            under <span className="mono">{relPath}</span>
                          </>
                        ) : (
                          " (no extra path)"
                        )}
                        . You do not need to enter{" "}
                        <span className="mono">public_html</span> or the
                        domain folder.
                      </span>
                      {finalUrl ? (
                        <div className="wa-final-url">
                          <span className="faint">Final URL</span>{" "}
                          <strong className="mono">{finalUrl}</strong>
                          {!pathConflict ? (
                            <span className="wa-path-ok">
                              ✓ Directory ready
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                      {pathFieldError ? (
                        <div className="wa-field-err-msg" role="alert">
                          ✕ {pathFieldError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="wa-section">
                  <h3 className="wa-section-title">Administrator</h3>
                  <div className="wa-fields wa-fields-3">
                    <div className="field wa-field">
                      <label>Username</label>
                      <input
                        value={props.adminUser}
                        onChange={(e) => props.setAdminUser(e.target.value)}
                        disabled={props.busy}
                        autoComplete="off"
                      />
                    </div>
                    <div className="field wa-field">
                      <label>Admin email</label>
                      <input
                        type="email"
                        value={props.adminEmail}
                        onChange={(e) => props.setAdminEmail(e.target.value)}
                        placeholder="admin@example.com"
                        disabled={props.busy}
                      />
                    </div>
                    <div className="field wa-field">
                      <label>Password</label>
                      <div className="wa-pass-row">
                        <input
                          type={showPass ? "text" : "password"}
                          value={props.adminPassword}
                          onChange={(e) =>
                            props.setAdminPassword(e.target.value)
                          }
                          placeholder="auto if empty"
                          disabled={props.busy}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={props.busy}
                          onClick={() =>
                            props.setAdminPassword(generatePassword())
                          }
                        >
                          Generate
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={props.busy}
                          onClick={() => setShowPass((v) => !v)}
                        >
                          {showPass ? "Hide" : "Show"}
                        </button>
                        {props.adminPassword ? (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() =>
                              void copyText(props.adminPassword)
                            }
                          >
                            Copy
                          </button>
                        ) : null}
                      </div>
                      <span
                        className={`wa-strength wa-strength-${strength.level}`}
                      >
                        {strength.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="wa-section">
                  <button
                    type="button"
                    className="wa-advanced-toggle"
                    onClick={() => setAdvancedOpen((v) => !v)}
                  >
                    {advancedOpen ? "▾" : "▸"} Advanced Settings
                  </button>
                  {advancedOpen ? (
                    <div className="wa-fields" style={{ marginTop: 12 }}>
                      <div className="field wa-field">
                        <label>Site title</label>
                        <input
                          value={props.siteTitle}
                          onChange={(e) =>
                            props.setSiteTitle(e.target.value)
                          }
                          placeholder={selected.name}
                          disabled={props.busy}
                        />
                      </div>
                      <p className="wa-field-hint">
                        Database name, user and password are created
                        automatically and connected to the application.
                      </p>
                    </div>
                  ) : null}
                </div>

                {props.busy ? (
                  <div className="wa-progress">
                    <h3 className="wa-section-title">
                      Installing {selected.name}
                    </h3>
                    <ol className="wa-steps">
                      {INSTALL_STEPS.map((label, i) => {
                        const done = progressStep > i + 1;
                        const current = progressStep === i + 1;
                        return (
                          <li
                            key={label}
                            className={
                              done
                                ? "is-done"
                                : current
                                  ? "is-current"
                                  : ""
                            }
                          >
                            <span className="wa-step-mark">
                              {done ? "✓" : current ? "●" : "○"}
                            </span>
                            {label}
                          </li>
                        );
                      })}
                    </ol>
                    <p className="wa-field-hint">
                      {Math.min(progressStep, INSTALL_STEPS.length)} /{" "}
                      {INSTALL_STEPS.length} steps
                    </p>
                  </div>
                ) : null}

                <div className="wa-summary">
                  <h3 className="wa-section-title">Installation Summary</h3>
                  <dl className="wa-summary-grid">
                    <div>
                      <dt>Application</dt>
                      <dd>{selected.name}</dd>
                    </div>
                    <div>
                      <dt>Domain</dt>
                      <dd className="mono">{props.domain || "—"}</dd>
                    </div>
                    <div>
                      <dt>Path</dt>
                      <dd className="mono">/{relPath || ""}</dd>
                    </div>
                    <div>
                      <dt>Version</dt>
                      <dd>{selected.version || "Latest stable"}</dd>
                    </div>
                    <div>
                      <dt>Admin</dt>
                      <dd className="mono">{props.adminUser || "admin"}</dd>
                    </div>
                    <div>
                      <dt>SSL</dt>
                      <dd>
                        {selectedSite?.sslEnabled
                          ? "Enabled"
                          : "Recommended"}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="wa-cta-row">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={
                      props.busy || !props.domain || pathConflict
                    }
                  >
                    {props.busy
                      ? `Installing ${selected.name}…`
                      : `Install ${selected.name}`}
                  </button>
                </div>
              </form>
            ) : (
              <div className="wa-empty-main">
                Select an application from the list to configure installation.
              </div>
            )}
          </section>
        </div>
      ) : null}

      {props.userId && tab === "installed" ? (
        <div className="wa-installed">
          <div className="wa-installed-head">
            <div>
              <h2 className="wa-panel-title">Installed Applications</h2>
              <p className="wa-panel-meta">
                {props.installed.length} installation
                {props.installed.length === 1 ? "" : "s"} · {activeCount}{" "}
                active
                {updates.length
                  ? ` · ${updates.length} update${updates.length === 1 ? "" : "s"} available`
                  : ""}
              </p>
              <p className="wa-panel-meta" style={{ marginTop: 4 }}>
                <strong>WP Admin</strong> opens WordPress admin with a one-time
                auto-login link (expires in 30 minutes).
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setTab("browse")}
            >
              Install App
            </button>
          </div>
          {ssoNote ? (
            <div className="wa-alert wa-alert-ok" style={{ marginBottom: 12 }}>
              {ssoNote}
            </div>
          ) : null}
          <div className="table-wrap wa-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Domain</th>
                  <th>Path</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Updates</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {props.installed.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="faint">
                      No website apps installed yet.
                    </td>
                  </tr>
                ) : (
                  props.installed.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <button
                          type="button"
                          className="wa-linkish"
                          onClick={() => setDrawerId(a.id)}
                        >
                          <strong>{a.appName}</strong>
                        </button>
                      </td>
                      <td className="mono faint">{a.domain}</td>
                      <td className="mono faint">
                        {a.path && a.path !== "/" ? `/${a.path}` : "/"}
                      </td>
                      <td className="mono faint">{a.version}</td>
                      <td>
                        <span className="badge status-active">{a.status}</span>
                      </td>
                      <td className="faint" style={{ fontSize: 12 }}>
                        {a.updateAvailable
                          ? a.latestVersion
                            ? `→ ${a.latestVersion}`
                            : "Available"
                          : "—"}
                      </td>
                      <td>
                        <div className="wa-row-actions">
                          <a
                            className="btn btn-sm"
                            href={`https://${a.domain}${
                              a.path && a.path !== "/" ? `/${a.path}` : ""
                            }/`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open
                          </a>
                          {(a.appId === "wordpress" ||
                            a.appName?.toLowerCase() === "wordpress") && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={ssoBusyId === a.id}
                              onClick={() => void openWpAdminSso(a.id)}
                              title="Auto-login to WP Admin (link expires in 30 minutes)"
                            >
                              {ssoBusyId === a.id ? "…" : "WP Admin"}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setDrawerId(a.id)}
                          >
                            Manage
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger-soft"
                            onClick={() =>
                              void props.onUninstall(a.id, a.appName)
                            }
                          >
                            Uninstall
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {props.userId && tab === "updates" ? (
        <div className="wa-installed">
          <div className="wa-installed-head">
            <div>
              <h2 className="wa-panel-title">Updates</h2>
              <p className="wa-panel-meta">
                {updates.length
                  ? `${updates.length} update${updates.length === 1 ? "" : "s"} available`
                  : "All applications are up to date."}
              </p>
            </div>
          </div>
          {updates.length === 0 ? (
            <div className="wa-empty-main">No updates available.</div>
          ) : (
            <div className="table-wrap wa-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Application</th>
                    <th>Location</th>
                    <th>Current</th>
                    <th>Latest</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {updates.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <strong>{a.appName}</strong>
                      </td>
                      <td className="mono faint">
                        {a.domain}
                        {a.path && a.path !== "/" ? `/${a.path}` : ""}
                      </td>
                      <td className="mono">{a.version}</td>
                      <td className="mono">{a.latestVersion || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => setDrawerId(a.id)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {drawer ? (
        <div
          className="wa-drawer-backdrop"
          onClick={() => setDrawerId(null)}
          role="presentation"
        >
          <aside
            className="wa-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`${drawer.appName} details`}
          >
            <header className="wa-drawer-head">
              <div>
                <h2 className="wa-panel-title">{drawer.appName}</h2>
                <p className="wa-panel-meta">
                  <span className="wa-status-dot" /> {drawer.status}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setDrawerId(null)}
              >
                Close
              </button>
            </header>
            <dl className="wa-drawer-dl">
              <div>
                <dt>Domain</dt>
                <dd className="mono">{drawer.domain}</dd>
              </div>
              <div>
                <dt>Path</dt>
                <dd className="mono">
                  {drawer.path && drawer.path !== "/"
                    ? `/${drawer.path}`
                    : "/"}
                </dd>
              </div>
              <div>
                <dt>Version</dt>
                <dd>{drawer.version}</dd>
              </div>
              <div>
                <dt>Database</dt>
                <dd className="mono">{drawer.databaseName || "—"}</dd>
              </div>
              <div>
                <dt>Admin user</dt>
                <dd className="mono">{drawer.adminUser || "—"}</dd>
              </div>
              <div>
                <dt>Installed</dt>
                <dd>
                  {drawer.createdAt
                    ? new Date(drawer.createdAt).toLocaleString()
                    : "—"}
                </dd>
              </div>
            </dl>
            <div className="wa-drawer-actions">
              <a
                className="btn btn-primary btn-sm"
                href={`https://${drawer.domain}${
                  drawer.path && drawer.path !== "/"
                    ? `/${drawer.path}`
                    : ""
                }/`}
                target="_blank"
                rel="noreferrer"
              >
                Open Website
              </a>
              {(drawer.appId === "wordpress" ||
                drawer.appName?.toLowerCase() === "wordpress") && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={ssoBusyId === drawer.id}
                  onClick={() => void openWpAdminSso(drawer.id)}
                >
                  {ssoBusyId === drawer.id
                    ? "Opening…"
                    : "WP Admin (auto-login)"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-danger-soft"
                onClick={() => {
                  setDrawerId(null);
                  void props.onUninstall(drawer.id, drawer.appName);
                }}
              >
                Uninstall
              </button>
            </div>
            <p className="wa-field-hint" style={{ marginTop: 12 }}>
              Auto-login links are one-time and expire after 30 minutes. You must
              be logged into RealPanel to generate them.
            </p>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
